import dayjs from "dayjs";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useAdAgencyShortEditMutation,
  useCreateAndSendProjectMutation,
  useCreateProjectMutation,
  useUpdateAndSendProjectMutation,
  useUpdateProjectMutation,
} from "../../api/projectsApi";
import Button, { ButtonModeE } from "../../components/Button/Button";
import Tooltip from "../../components/Tooltip/Tooltip";
import { ProjectStatusE, StatusE, UserRoleE } from "../../models/enums";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectCurrentUser } from "../../store/selectors/authSelectors";
import {
  selectDeliveryWizardData,
  selectIsDeliveryWizardShortEdit,
} from "../../store/selectors/deliveryWizardSelector";
import {
  setNextStep,
  setPrevStep,
} from "../../store/slices/deliveryWizardSlice";
import {
  DEFAULT_ADVERTISER_AGENCY_ID,
  DEFAULT_POST_PRODUCTION_COMPANY_ID,
  DEFAULT_PRODUCTION_COMPANY_ID,
} from "../../utils/constants";
import { TOAST_DURATION } from "../../utils/helpers/constants";
import { isMobileOrTablet } from "../../utils/helpers/mediaQueries";
import usePopup from "../../utils/hooks/usePopup";
import { useTranslate } from "../../utils/hooks/useTranslate";
import { WizardModeE } from "./models";

import styles from "./styles.module.scss";

type DeliveryWizardFooterPropTypes = {
  mode: WizardModeE;
};

const DeliveryWizardFooter: FC<DeliveryWizardFooterPropTypes> = (props) => {
  const user = useAppSelector(selectCurrentUser);
  const { mode } = props;
  const dispatch = useAppDispatch();
  const { removePopup } = usePopup();
  const {
    deliveryWizardStep,
    editableVersionIndex,
    delivery,
    campaignDetails,
  } = useAppSelector(selectDeliveryWizardData);
  const isShortEdit = useAppSelector(selectIsDeliveryWizardShortEdit);
  const { removeAllPopups } = usePopup();
  const [andSend, setAndSend] = useState(-1);
  const { t } = useTranslate();
  const navigate = useNavigate();

  const [createProject] = useCreateProjectMutation();
  const [createAndSendProject] = useCreateAndSendProjectMutation();
  const [updateProject] = useUpdateProjectMutation();
  const [updateAndSendProject] = useUpdateAndSendProjectMutation();
  const [shortUpdate] = useAdAgencyShortEditMutation();

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const NEXT = "ArrowRight";
    const PREV = "ArrowLeft";
    const continueButton = document.getElementById("continue-button");
    const backButton = document.getElementById("back-button");
    const isInputEdit = document.activeElement.tagName === "INPUT";

    if (e.key === NEXT && !isInputEdit) {
      continueButton?.click();
    }

    if (e.key === PREV && !isInputEdit) {
      backButton?.click();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleBack = () => {
    if (deliveryWizardStep === 1) {
      removePopup();
    } else {
      dispatch(setPrevStep());
    }
  };

  const handleNext = (
    e:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.FormEvent<HTMLFormElement>
  ) => {
    if (deliveryWizardStep === 1) return;

    if (deliveryWizardStep === 2 && editableVersionIndex !== -1) {
      const saveButton = document.getElementById("save-house-numbers-button");
      saveButton?.click();
      dispatch(setNextStep());
    } else {
      dispatch(setNextStep());
    }
  };

  const formBody = () => ({
    ...delivery,
    Name: campaignDetails.Campaign.Name,
    AdvertiserId: String(campaignDetails.AdvertiserId),
    BrandId: String(campaignDetails.BrandId),
    AdvertisingAgencyName: delivery.AdvertisingAgencyName,
    AdvertisingAgencyId:
      user.role === UserRoleE.Broadcaster
        ? DEFAULT_ADVERTISER_AGENCY_ID
        : user.id,
    BroadcastingDate: delivery.BroadcastingDate,
    IndustryId: String(campaignDetails.IndustryId),
    PostProductionCompanyId:
      user.role === UserRoleE.Broadcaster
        ? DEFAULT_POST_PRODUCTION_COMPANY_ID
        : delivery.PostProductionCompanyId ||
          DEFAULT_POST_PRODUCTION_COMPANY_ID,
    ProductionCompanyId:
      delivery.ProductionCompanyId || DEFAULT_PRODUCTION_COMPANY_ID,
    addToExist: campaignDetails.Campaign.Id,
    Id: mode === WizardModeE.Create ? 0 : delivery.Id,
    ProjectComment: delivery.ProjectComment,
    ProjectType:
      user.role === UserRoleE.Broadcaster ? "VideoAdBroadcaster" : "VideoAd",
    campaignRetry: true,
    IsCopyOperation: false,
    Campaign: {
      Id: "",
      CampaignId: campaignDetails.Campaign.CampaignId,
    },
    broadcasters: [],
  });

  const handleSaveDelivery = async (andSend: boolean) => {
    const body: any = formBody();

    setAndSend(andSend ? 1 : 0);
    let res;

    if (andSend) {
      res = await createAndSendProject(body);
    } else {
      res = await createProject(body);
    }

    if (res.error) {
      toast.error(res.error.data?.Message, {
        autoClose: TOAST_DURATION,
      });
      return;
    }

    if (res.data) {
      removeAllPopups();
      toast.success(t`Delivery created successfully`, {
        autoClose: TOAST_DURATION,
      });
      if (user.role === UserRoleE.DeliveryService) {
        navigate(`/deliveries/${res.data.Id}`); // JMMEDIAT-441
      } else {
        navigate(`/campaigns/${res.data?.Detailed?.Campaign?.Id}`);
      }
    }
  };

  const handleUpdateDelivery = async (andSend: boolean) => {
    const body: any = formBody();
    let res: any;

    if (isShortEdit) {
      res = await shortUpdate(body);
    } else {
      if (user.role === UserRoleE.Broadcaster) {
        delete body.AllowEditHouseNumber;
        delete body.ApprovalDate;
        delete body.ApprovedByUserId;
        delete body.CreatedByUserId;
        delete body.CreationDateTime;
        delete body.DeliveryServiceDetailsViewModel;
        delete body.DeliveryServiceDetailsViewModel;
        delete body.Detailed;
        delete body.EditErrorMessage;
        delete body.EnumDescriptionItemList;
        delete body.IsCampaignEdit;
        delete body.IsCampaignCreation;
        delete body.IsEditable;
        delete body.Number;
        delete body.NumberForClient;
        delete body.PostProductionCompanyInternalNumber;
        delete body.ProjectTypeString;
        delete body.Status;
        delete body.StatusName;
        delete body.SupplierId;
        delete body.blockSensetiveEdit;
      }

      setAndSend(andSend ? 1 : 0);

      if (
        user.role !== UserRoleE.GlobalAdmin &&
        user.role !== UserRoleE.GlobalAdminAndAdminContentManager &&
        user.role !== UserRoleE.Broadcaster &&
        (andSend ? andSend : delivery.StatusName !== StatusE.Creating)
      ) {
        res = await updateAndSendProject({
          ...body,
          Status: ProjectStatusE.Opened,
        });
      } else {
        res = await updateProject({ ...body, Status: ProjectStatusE.Creating });
      }
    }

    if (res.data) {
      removeAllPopups();
      toast.success(t`Delivery updated successfully`, {
        autoClose: TOAST_DURATION,
      });
    }
  };

  return (
    <div className={styles.wizardFooter}>
      <Button mode={ButtonModeE.OUTLINED} onClick={handleBack} id="back-button">
        Back
      </Button>
      {deliveryWizardStep === 3 && user.role === UserRoleE.DeliveryService && (
        // <div className={styles.buttonsGroup}>
        <Button
          useGlobalLoading
          disabled={
            !delivery?.BroadcasterIds?.split(",")?.filter((item) => item)
              ?.length
          }
          onClick={() => handleSaveDelivery(true)}
          // icon='send'
        >
          {isMobileOrTablet() ? "Create the delivery" : "Create delivery"}
        </Button>
        // </div>
      )}
      {deliveryWizardStep === 3 &&
        mode !== WizardModeE.Edit &&
        user.role !== UserRoleE.DeliveryService && (
          // <div className={styles.buttonsGroupThirdStep}>
          <>
            {andSend === 0 || andSend === -1 ? (
              isMobileOrTablet() ? (
                <Button
                  useGlobalLoading
                  disabled={
                    !delivery?.BroadcasterIds?.split(",")?.filter(
                      (item) => item
                    )?.length
                  }
                  onClick={() => handleSaveDelivery(false)}
                  icon="save"
                  mode={ButtonModeE.OUTLINED}
                >
                  {user.role === UserRoleE.Broadcaster
                    ? "Save"
                    : "Save delivery"}
                </Button>
              ) : (
                <Tooltip text="By saving this delivery for later use, the delivery will be saved with the status 'Creating' and will not be sent to the post-production and broadcasters. You can always go back to edit the delivery and choose 'Save and send delivery' so it will be sent to other participants.">
                  <Button
                    useGlobalLoading
                    disabled={
                      !delivery?.BroadcasterIds?.split(",")?.filter(
                        (item) => item
                      )?.length
                    }
                    onClick={() => handleSaveDelivery(false)}
                    icon="save"
                    mode={ButtonModeE.OUTLINED}
                  >
                    {user.role === UserRoleE.Broadcaster
                      ? "Save"
                      : "Save delivery"}
                  </Button>
                </Tooltip>
              )
            ) : null}
            {(andSend === 1 || andSend === -1) &&
              user.role !== UserRoleE.Broadcaster && (
                <Button
                  useGlobalLoading
                  disabled={
                    !delivery?.BroadcasterIds?.split(",")?.filter(
                      (item) => item
                    )?.length
                  }
                  onClick={() => handleSaveDelivery(true)}
                  icon="send"
                >
                  Save and send delivery
                </Button>
              )}
          </>
          // </div>
        )}
      {(mode === WizardModeE.Edit || deliveryWizardStep !== 3) &&
        user.role !== UserRoleE.GlobalAdminAndAdminContentManager &&
        user.role !== UserRoleE.MediaCompany && (
          //   <div className={styles.buttonsGroup}>
          <>
            {mode === WizardModeE.Edit && (andSend === 0 || andSend === -1) && (
              <Button
                disabled={
                  editableVersionIndex !== -1 ||
                  !delivery?.BroadcasterIds?.split(",")?.length
                }
                useGlobalLoading
                mode={ButtonModeE.OUTLINED}
                icon="save"
                onClick={() => handleUpdateDelivery(false)}
              >
                Save changes
              </Button>
            )}
            {mode === WizardModeE.Edit &&
              delivery.StatusName === StatusE.Creating &&
              (andSend === 1 || andSend === -1) && (
                <Button
                  disabled={
                    editableVersionIndex !== -1 ||
                    !delivery?.BroadcasterIds?.split(",")?.length
                  }
                  useGlobalLoading
                  mode={ButtonModeE.OUTLINED}
                  icon="send"
                  onClick={() => handleUpdateDelivery(true)}
                >
                  Save changes and send
                </Button>
              )}
            {((((isShortEdit &&
              deliveryWizardStep !== 2 &&
              deliveryWizardStep !== 3) ||
              (!isShortEdit && deliveryWizardStep !== 3)) &&
              delivery.StatusName !== StatusE.ApprovedAndSentToBroadcaster &&
              delivery.StatusName !== StatusE.DownloadedByAllBroadcasters &&
              delivery.StatusName !== StatusE.DownloadedByBroadcaster) ||
              mode === WizardModeE.Clone ||
              mode === WizardModeE.Create) && (
              <Button
                form={deliveryWizardStep === 1 ? "delivery-first-step" : ""}
                onClick={handleNext}
                id="continue-button"
              >
                Continue
              </Button>
            )}
          </>
          //   </div>
        )}
    </div>
  );
};

export default DeliveryWizardFooter;
